/**
 * 处理referrer信息并转换为UTM参数
 * 根据需求将站外地址导入到utm参数中，上传到神策
 */

// 搜索引擎列表，这些来源会被标记为organic
const SEARCH_ENGINES = [
  'google.com',
  'baidu.com',
  'bing.com',
  'yandex.com',
  'duckduckgo.com',
  'yahoo.com',
  'so.com',
  'naver.com',
  'seznam.cz',
];

// 需要排除的Google域名（解决谷歌登录干扰问题）
const EXCLUDED_DOMAINS = ['accounts.google.com'];

// 需要标记为direct的域名
const DIRECT_DOMAINS = ['vidu.studio', 'vidu.com', 'vidu.zone'];

/**
 * 从referrer URL中提取域名
 * @param url referrer URL
 * @returns 提取的域名
 */
export const extractDomain = (url: string): string => {
  try {
    // 移除协议部分
    let domain = url.replace(/^(https?:\/\/)?(www\.)?/, '');
    // 只保留域名部分（移除路径和查询参数）
    domain = domain.split('/')[0];
    return domain.toLowerCase();
  } catch (error) {
    console.error('Error extracting domain:', error);
    return '';
  }
};

/**
 * 检查URL是否来自搜索引擎
 * @param domain 域名
 * @returns 是否是搜索引擎
 */
export const isSearchEngine = (domain: string): boolean => {
  return SEARCH_ENGINES.some((engine) => domain.includes(engine));
};

/**
 * 检查URL是否应该被排除
 * @param domain 域名
 * @returns 是否应该被排除
 */
export const isExcludedDomain = (domain: string): boolean => {
  return EXCLUDED_DOMAINS.some((excluded) => domain.includes(excluded));
};

/**
 * 检查URL是否应该被标记为direct
 * @param domain 域名
 * @returns 是否应该被标记为direct
 */
export const isDirectDomain = (domain: string): boolean => {
  return DIRECT_DOMAINS.some((direct) => domain.includes(direct));
};

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const UTM_SESSION_KEY = 'utm_params';

/**
 * 获取UTM参数对象
 * @returns UTM参数对象
 */
export const getExistingUtmParams = (): Record<string, string> => {
  const searchParams = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  UTM_KEYS.forEach((param) => {
    const value = searchParams.get(param);
    if (value) {
      utmParams[param] = value;
    }
  });

  return utmParams;
};

const saveUtmToSession = (params: Record<string, string>) => {
  try {
    sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(params));
  } catch {}
};

const getUtmFromSession = (): Record<string, string> | null => {
  try {
    const stored = sessionStorage.getItem(UTM_SESSION_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

/**
 * 处理referrer信息并生成UTM参数
 * @returns UTM参数对象
 */
export const processReferrer = (): Record<string, string> => {
  // 首先检查URL中是否已有UTM参数
  const existingUtmParams = getExistingUtmParams();

  // 如果已有utm_source或utm_medium，则不覆盖（优先使用URL中的参数），并持久化到session
  if (existingUtmParams.utm_source || existingUtmParams.utm_medium) {
    saveUtmToSession(existingUtmParams);
    return existingUtmParams;
  }

  // URL中没有UTM参数时，优先读取session中已存储的值（防止站内跳转后丢失）
  const sessionUtmParams = getUtmFromSession();
  if (sessionUtmParams?.utm_source || sessionUtmParams?.utm_medium) {
    return sessionUtmParams;
  }

  // 获取referrer
  const referrer = document.referrer;

  // 如果没有referrer，返回空对象或标记为unknown
  if (!referrer) {
    return existingUtmParams; // 保留现有参数，不添加新的
  }

  const domain = extractDomain(referrer);

  // 如果是被排除的域名，不处理
  if (isExcludedDomain(domain)) {
    return existingUtmParams;
  }

  // 如果是direct域名，标记为direct
  if (isDirectDomain(domain)) {
    return {
      ...existingUtmParams,
      utm_source: 'direct',
      utm_medium: 'direct',
    };
  }

  // 如果是搜索引擎，标记为organic
  if (isSearchEngine(domain)) {
    return {
      ...existingUtmParams,
      utm_source: domain,
      utm_medium: 'organic',
    };
  }

  // 其他来源，将域名作为utm_source，medium标记为referral
  return {
    ...existingUtmParams,
    utm_source: domain,
    utm_medium: 'referral',
  };
};
